import React, { useState, useRef, useEffect } from 'react';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string, mentions: string[]) => void;
  placeholder?: string;
  companyId: string;
  accessToken: string;
  rows?: number;
  className?: string;
}

export function MentionTextarea({
  value,
  onChange,
  placeholder,
  companyId,
  accessToken,
  rows = 4,
  className,
}: MentionTextareaProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const editableRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Log props on mount
  useEffect(() => {
    console.log('[MentionTextarea] 🎬 Component mounted with props:');
    console.log('[MentionTextarea] - companyId:', companyId);
    console.log('[MentionTextarea] - accessToken (first 20):', accessToken?.substring(0, 20));
    console.log('[MentionTextarea] - projectId:', projectId);
  }, []);

  // Debug logging for state changes
  useEffect(() => {
    console.log('[MentionTextarea] 🔄 State update - showSuggestions:', showSuggestions, ', suggestions count:', suggestions.length, ', will show dropdown:', showSuggestions && suggestions.length > 0);
  }, [showSuggestions, suggestions]);

  // Extract mentioned user IDs from text
  const extractMentions = (text: string): string[] => {
    // Format: @[name]{{id}}
    const mentionRegex = /@\[([^\]]+)\]\{\{([^}]+)\}\}/g;
    const mentions: string[] = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[2]); // User ID is in the second capture group
    }
    return mentions;
  };

  // Search for users
  const searchUsers = async (query: string) => {
    console.log('[MentionTextarea] 🔍 Searching users with query:', query);
    console.log('[MentionTextarea] CompanyId:', companyId);
    console.log('[MentionTextarea] AccessToken (first 20):', accessToken?.substring(0, 20));
    
    try {
      // Check if companyId looks like a firm ID (e.g., "demo-firm-premium")
      // If so, use the special support mentionable users endpoint
      const isFirmId = companyId.includes('firm') || companyId.includes('consulting');
      
      const endpoint = isFirmId
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${companyId}/mentionable-users`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/users`;
      
      console.log('[MentionTextarea] Using endpoint:', endpoint);
      
      const response = await fetch(
        endpoint,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      console.log('[MentionTextarea] 📡 Search response status:', response.status);
      
      if (!response.ok) {
        console.error('[MentionTextarea] ❌ Search failed:', response.statusText);
        return;
      }

      const data = await response.json();
      console.log('[MentionTextarea] ✅ Search results:', data);
      console.log('[MentionTextarea] 👥 Number of users found:', data.users?.length || 0);
      
      // Filter users by query (case-insensitive)
      const filtered = (data.users || []).filter((user: User) => {
        const searchTerm = query.toLowerCase();
        return (
          user.name?.toLowerCase().includes(searchTerm) ||
          user.email?.toLowerCase().includes(searchTerm) ||
          user.role?.toLowerCase().includes(searchTerm)
        );
      });
      
      console.log('[MentionTextarea] Setting suggestions to:', filtered);
      setSuggestions(filtered);
    } catch (error) {
      console.error('[MentionTextarea] ❌ Error searching users:', error);
    }
  };

  // Get text content from contenteditable
  const getTextContent = (): string => {
    if (!editableRef.current) return '';
    
    let text = '';
    editableRef.current.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.classList.contains('mention')) {
          const name = element.textContent?.replace('@', '') || '';
          const userId = element.getAttribute('data-user-id') || '';
          text += `@[${name}]{{${userId}}}`;
        } else {
          text += element.textContent;
        }
      }
    });
    return text;
  };

  // Handle input in contenteditable
  const handleInput = () => {
    const text = getTextContent();
    console.log('[MentionTextarea] Text changed:', text);
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const textBeforeCursor = range.startContainer.textContent?.substring(0, range.startOffset) || '';
    
    console.log('[MentionTextarea] Text before cursor:', textBeforeCursor);

    // Check if we're typing after an @
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfter = textBeforeCursor.substring(lastAtIndex + 1);
      console.log('[MentionTextarea] Text after @:', textAfter);
      
      // Only show suggestions if there's no space after @
      if (!textAfter.includes(' ') && !textAfter.includes('\n')) {
        console.log('[MentionTextarea] Showing suggestions for query:', textAfter);
        setShowSuggestions(true);
        searchUsers(textAfter);
        setSelectedIndex(0);
      } else {
        console.log('[MentionTextarea] Space found, hiding suggestions');
        setShowSuggestions(false);
      }
    } else {
      console.log('[MentionTextarea] No @ found, hiding suggestions');
      setShowSuggestions(false);
    }

    const mentions = extractMentions(text);
    onChange(text, mentions);
  };

  // Insert mention when user selects from suggestions
  const insertMention = (user: User) => {
    console.log('[MentionTextarea] 💡 Inserting mention for user:', user.name);
    
    if (!editableRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    
    // Find and remove the @ and any text after it
    const textNode = range.startContainer;
    if (textNode.nodeType === Node.TEXT_NODE && textNode.textContent) {
      const textBeforeCursor = textNode.textContent.substring(0, range.startOffset);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');
      
      if (lastAtIndex !== -1) {
        // Delete from @ to cursor
        const deleteRange = document.createRange();
        deleteRange.setStart(textNode, lastAtIndex);
        deleteRange.setEnd(textNode, range.startOffset);
        deleteRange.deleteContents();

        // Create mention span
        const mentionSpan = document.createElement('span');
        mentionSpan.className = 'mention bg-blue-100 text-blue-800 px-1 rounded font-medium';
        mentionSpan.contentEditable = 'false';
        mentionSpan.setAttribute('data-user-id', user.id);
        mentionSpan.textContent = `@${user.name}`;

        // Insert mention and a space
        deleteRange.insertNode(mentionSpan);
        
        const spaceNode = document.createTextNode(' ');
        mentionSpan.parentNode?.insertBefore(spaceNode, mentionSpan.nextSibling);

        // Move cursor after the space
        const newRange = document.createRange();
        newRange.setStartAfter(spaceNode);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }

    // Close suggestions
    setShowSuggestions(false);
    
    // Update the value by getting the current text content
    const text = getTextContent();
    const mentions = extractMentions(text);
    onChange(text, mentions);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && showSuggestions) {
      e.preventDefault();
      insertMention(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Update contenteditable when value changes externally
  useEffect(() => {
    if (!editableRef.current) return;
    
    const currentText = getTextContent();
    if (currentText !== value && !showSuggestions) {
      // Render the value with styled mentions
      const rendered = value.split(/(@\[[^\]]+\]\{\{[^}]+\}\})/g).map((part, index) => {
        const mentionMatch = part.match(/@\[([^\]]+)\]\{\{([^}]+)\}\}/);
        if (mentionMatch) {
          return `<span class="mention bg-blue-100 text-blue-800 px-1 rounded font-medium" contenteditable="false" data-user-id="${mentionMatch[2]}">@${mentionMatch[1]}</span>`;
        }
        return part;
      }).join('');
      
      editableRef.current.innerHTML = rendered || '';
    }
  }, [value]);

  return (
    <div className="relative">
      {/* Contenteditable div */}
      <div
        ref={editableRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className={`${className} min-h-16 w-full rounded-md border border-input bg-input-background px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-[3px] focus:ring-ring/50 focus:border-ring`}
        style={{
          minHeight: `${rows * 1.5}rem`,
        }}
        data-placeholder={placeholder}
      />

      {/* Show placeholder when empty */}
      {!value && placeholder && (
        <div className="absolute top-0 left-0 px-3 py-2 text-gray-400 pointer-events-none">
          {placeholder}
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full max-w-md mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((user, index) => (
            <div
              key={user.id}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur
                console.log('[MentionTextarea] 🖱️ User clicked on:', user.name);
                insertMention(user);
              }}
            >
              <div className="font-medium">{user.name}</div>
              <div className="text-sm text-gray-500">{user.email}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper component to display text with rendered mentions
export function MentionDisplay({ text }: { text: string }) {
  if (!text) return null;

  // Support both formats:
  // - New format: @[name]{{id}}
  // - Old/markdown format: @[name]({{id}})
  const mentionRegex = /@\[([^\]]+)\](?:\()??\{\{([^}]+)\}\}(?:\))?/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push(
        <span key={key++}>{text.substring(lastIndex, match.index)}</span>
      );
    }
    // Add mention as styled span
    parts.push(
      <span key={key++} className="bg-blue-100 text-blue-800 px-1 rounded font-medium">
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.substring(lastIndex)}</span>);
  }

  return <>{parts}</>;
}